import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { StatusCertificadoEnum } from '@shared/enums/certificado/status-certificado.enum';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CertificadoEmpresaA1Service } from '@shared/services/apis/dfe/certificado_empresa_a1.service';
import { ConfiguracaoDFeGeralService } from '@shared/services/apis/dfe/configuracao-dfe-geral.service';
import { NotaFiscalSaidaService } from '@shared/services/apis/orquestrador-vendas/nota-fiscal-saida.service';
import { PrepararTransmissaoLoteNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/preparar-transmissao-lote-nota-fiscal-saida.request';
import { PrepararTransmissaoLoteNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/preparar-transmissao-lote-nota-fiscal-saida.response';
import { ElegivelTransmissaoNFeSaidaStruct } from '@shared/services/structs/repo-dfe/elegivel-transmissao-nfe-saida.struct';
import {
  TransmitirLoteNotaFiscalSaidaDialogData,
  TransmitirLoteNotaFiscalSaidaDialogResult,
} from './transmitir-lote-nota-fiscal-saida-dialog-data';

/** Etapas do modal (estado local da tela). */
enum EtapaTransmissaoLote {
  Inicio = 1,
  Enviando = 2,
  Resultado = 3,
}

/** Nota do lote que não foi para a fila: recusada pelo servidor ou não enviada porque o envio foi interrompido. */
interface NotaNaoEnviada {
  idNFeSaida: number;
  numero: number | null;
  motivos: string[];
}

/**
 * Modal "Lote de notas fiscais eletrônicas" (T14, sem lote na SEFAZ). Início: certificado, ambiente, opções e o resumo
 * do `transmitir/lote/preparar` (últimos 30 dias). Enviando: o `transmitir` nota a nota, em sequência; Interromper para
 * o laço e as notas não enviadas ficam como estavam. Resultado: "Não enviadas" (com o motivo e "Corrigir") e
 * "Enviadas para transmissão"; o resultado da SEFAZ chega depois, pela listagem.
 */
@Component({
  selector: 'app-transmitir-lote-nota-fiscal-saida-dialog',
  standalone: true,
  imports: [
    DatePipe,
    AbntPipe,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressBarModule,
    MatTabsModule,
    AlertCardComponent,
    EmptyStateComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './transmitir-lote-nota-fiscal-saida-dialog.component.html',
  styleUrls: ['./transmitir-lote-nota-fiscal-saida-dialog.component.css'],
})
export class TransmitirLoteNotaFiscalSaidaDialogComponent implements OnInit, OnDestroy {
  readonly etapaEnum = EtapaTransmissaoLote;
  readonly statusCertificadoEnum = StatusCertificadoEnum;

  etapa = EtapaTransmissaoLote.Inicio;
  isHomologacao = false;

  isCarregandoCertificado = true;
  statusCertificado: StatusCertificadoEnum | null = null;
  dataVencimentoCertificado: Date | null = null;

  isIncluirRejeitadas = true;
  isPreparando = true;
  lote: PrepararTransmissaoLoteNotaFiscalSaidaResponse | null = null;

  /** Posição da nota em envio (1 a total). */
  posicaoEnvio = 0;
  totalEnvio = 0;
  isInterrompido = false;
  enviadas: ElegivelTransmissaoNFeSaidaStruct[] = [];
  naoEnviadas: NotaNaoEnviada[] = [];

  /** Modal fechado no meio do envio (navegação): o laço para. */
  private isDestruido = false;

  constructor(
    private dialogRef: MatDialogRef<TransmitirLoteNotaFiscalSaidaDialogComponent, TransmitirLoteNotaFiscalSaidaDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: TransmitirLoteNotaFiscalSaidaDialogData,
    private notaFiscalSaidaService: NotaFiscalSaidaService,
    private certificadoEmpresaA1Service: CertificadoEmpresaA1Service,
    private configuracaoDFeGeralService: ConfiguracaoDFeGeralService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.carregarAmbiente();
    this.carregarCertificado();
    this.preparar();
  }

  ngOnDestroy(): void {
    this.isDestruido = true;
  }

  get subtitulo(): string {
    return this.data.isTodasAguardandoTransmissao ? 'Todas as aguardando transmissão' : 'Notas selecionadas';
  }

  get quantidade(): number {
    return this.lote?.quantidade ?? 0;
  }

  get isCertificadoComAviso(): boolean {
    return this.statusCertificado === StatusCertificadoEnum.ProximoDoVencimento
      || this.statusCertificado === StatusCertificadoEnum.VencimentoIminente;
  }

  get percentualEnvio(): number {
    return this.totalEnvio ? ((this.enviadas.length + this.naoEnviadas.length) / this.totalEnvio) * 100 : 0;
  }

  /** O lote não guarda a nota: o ambiente é o da configuração geral de notas fiscais. */
  private carregarAmbiente(): void {
    this.configuracaoDFeGeralService.get().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.isHomologacao = res.configuracaoDFeGeral?.idTipoAmbiente === TipoAmbienteEnum.Homologacao;
      },
      error: () => {
        this.alertService.show('Aviso', 'Não foi possível consultar o ambiente de emissão agora.', AlertType.warning);
      },
    });
  }

  /** Situação do certificado A1 da empresa: só informa; quem recusa a transmissão é a SEFAZ. */
  private carregarCertificado(): void {
    this.certificadoEmpresaA1Service.getCertificadoAtual().subscribe({
      next: res => {
        this.isCarregandoCertificado = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.statusCertificado = res.statusCertificado;
        this.dataVencimentoCertificado = res.certificadoEmpresaA1?.dataVencimento ?? null;
      },
      error: () => {
        this.isCarregandoCertificado = false;
        this.alertService.show('Aviso', 'Não foi possível consultar o certificado digital agora.', AlertType.warning);
      },
    });
  }

  alternarRejeitadas(isIncluir: boolean): void {
    this.isIncluirRejeitadas = isIncluir;
    this.preparar();
  }

  private preparar(): void {
    const request = new PrepararTransmissaoLoteNotaFiscalSaidaRequest();
    request.listaIdNFeSaida = this.data.isTodasAguardandoTransmissao ? [] : this.data.listaIdNFeSaida;
    request.isTodasAguardandoTransmissao = this.data.isTodasAguardandoTransmissao;
    request.isIncluirRejeitadas = this.isIncluirRejeitadas;

    this.isPreparando = true;
    this.notaFiscalSaidaService.prepararTransmissaoLote(request).subscribe({
      next: res => {
        this.isPreparando = false;
        if (res.isError) {
          this.lote = null;
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.lote = res;
      },
      error: () => {
        this.isPreparando = false;
        this.lote = null;
        this.alertService.show('Aviso', 'Não foi possível preparar o lote agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  enviar(): void {
    const fila = this.lote?.listaElegivel ?? [];
    if (!fila.length) return;

    this.etapa = EtapaTransmissaoLote.Enviando;
    this.totalEnvio = fila.length;
    this.isInterrompido = false;
    this.enviadas = [];
    this.naoEnviadas = [];
    this.enviarNota(fila, 0);
  }

  /** Uma nota por vez: a próxima só sai depois da resposta da anterior. */
  private enviarNota(fila: ElegivelTransmissaoNFeSaidaStruct[], indice: number): void {
    if (this.isDestruido) return;

    if (this.isInterrompido || indice >= fila.length) {
      fila.slice(indice).forEach(nota => this.naoEnviadas.push({
        idNFeSaida: nota.idNFeSaida,
        numero: nota.numero,
        motivos: ['O envio foi interrompido antes desta nota. Ela continua como estava.'],
      }));
      this.etapa = EtapaTransmissaoLote.Resultado;
      return;
    }

    const nota = fila[indice];
    this.posicaoEnvio = indice + 1;

    this.notaFiscalSaidaService.transmitir(nota.idNFeSaida).subscribe({
      next: res => {
        if (res.isError) {
          this.naoEnviadas.push({
            idNFeSaida: nota.idNFeSaida,
            numero: nota.numero,
            motivos: res.listaPendencia?.length ? res.listaPendencia : [res.errorDescription],
          });
        } else {
          this.enviadas.push(nota);
        }
        this.enviarNota(fila, indice + 1);
      },
      error: () => {
        this.naoEnviadas.push({
          idNFeSaida: nota.idNFeSaida,
          numero: nota.numero,
          motivos: ['Não foi possível transmitir a nota agora. Tente novamente em alguns instantes.'],
        });
        this.enviarNota(fila, indice + 1);
      },
    });
  }

  /** Para o laço depois da nota em envio. */
  interromper(): void {
    this.isInterrompido = true;
  }

  corrigir(nota: NotaNaoEnviada): void {
    this.dialogRef.close({ idNFeSaidaCorrigir: nota.idNFeSaida });
  }

  fechar(): void {
    if (this.etapa === EtapaTransmissaoLote.Enviando) return;
    this.dialogRef.close({ idNFeSaidaCorrigir: null });
  }

  rotuloNota(numero: number | null): string {
    return numero != null ? `NF-e ${numero}` : 'NF-e sem número';
  }
}
